import React, { useState } from 'react';
import { Box, Chip, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Error as ErrorIcon, Warning as WarningIcon, Close as CloseIcon, DeleteSweep as DeleteSweepIcon } from '@mui/icons-material';
import { adminAPI } from '../../../utils/api';

const ErrorTrackingTab = ({ metrics }) => {
  const syncErrors = metrics.managerIntel.recentErrors;
  const syncErrorCount = metrics.managerIntel.errorCount;
  const jobErrors = metrics.scheduledJobs?.recentErrors || [];
  const jobErrorCount = metrics.scheduledJobs?.errorCount || 0;
  const totalErrorCount = syncErrorCount + jobErrorCount;
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState({ title: '', message: '' });

  const handleClearAll = async () => {
    setLoading(true);
    try {
      // Clear both sync errors and scheduled job errors
      await Promise.all([
        adminAPI.clearAllErrors(),
        adminAPI.clearAllScheduledJobErrors(),
      ]);
      setDialogContent({
        title: '✅ Errors Cleared',
        message: 'All errors cleared successfully',
      });
      setDialogOpen(true);
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      setDialogContent({
        title: '❌ Error',
        message: 'Failed to clear errors',
      });
      setDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClearError = async (username) => {
    setLoading(true);
    try {
      const res = await adminAPI.clearError(username);
      setDialogContent({
        title: '✅ Error Dismissed',
        message: res.data.message,
      });
      setDialogOpen(true);
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      setDialogContent({
        title: '❌ Error',
        message: 'Failed to dismiss error',
      });
      setDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClearJobError = async (errorId) => {
    setLoading(true);
    try {
      const res = await adminAPI.clearScheduledJobError(errorId);
      setDialogContent({
        title: '✅ Error Dismissed',
        message: res.data.message,
      });
      setDialogOpen(true);
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      setDialogContent({
        title: '❌ Error',
        message: 'Failed to dismiss error',
      });
      setDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Error Summary */}
      <Box
        sx={{
          background: totalErrorCount > 0 ? '#7F1D1D' : '#064E3B',
          border: `2px solid ${totalErrorCount > 0 ? '#EF4444' : '#10B981'}`,
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: totalErrorCount > 0 ? '16px' : '0' }}>
          <Box>
            <Box sx={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>
              {totalErrorCount > 0 ? '🚨 Active Errors Detected' : '✅ No Active Errors'}
            </Box>
            <Box sx={{ fontSize: '16px', color: '#D1D5DB' }}>
              {syncErrorCount} sync error{syncErrorCount !== 1 ? 's' : ''} · {jobErrorCount} job error{jobErrorCount !== 1 ? 's' : ''} · {syncErrors.length + jobErrors.length} recent failures shown
            </Box>
          </Box>
          <Box sx={{ fontSize: '64px' }}>
            {totalErrorCount > 0 ? <ErrorIcon sx={{ fontSize: '64px', color: '#EF4444' }} /> : '🎉'}
          </Box>
        </Box>

        {totalErrorCount > 0 && (
          <Button
            variant="contained"
            startIcon={<DeleteSweepIcon />}
            onClick={handleClearAll}
            disabled={loading}
            sx={{
              background: '#EF4444',
              color: '#FFF',
              fontWeight: 700,
              padding: '12px 24px',
              '&:hover': {
                background: '#DC2626',
              },
              '&:disabled': {
                background: '#374151',
                color: '#6B7280',
              },
            }}
          >
            Clear All Errors
          </Button>
        )}
      </Box>

      {/* Error List */}
      {(syncErrors.length > 0 || jobErrors.length > 0) ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Scheduled Job Errors */}
          {jobErrors.map((error, index) => (
            <Box
              key={error.id}
              sx={{
                background: '#1E293B',
                border: '2px solid #7F1D1D',
                borderRadius: '12px',
                padding: '24px',
                position: 'relative',
                '&:hover': {
                  borderColor: '#EF4444',
                  boxShadow: '0 8px 24px #EF444444',
                },
              }}
            >
              {/* Dismiss Button */}
              <IconButton
                onClick={() => handleClearJobError(error.id)}
                disabled={loading}
                sx={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  color: '#94A3B8',
                  '&:hover': {
                    color: '#EF4444',
                    background: '#7F1D1D',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>

              {/* Error Number Badge */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '16px',
                  right: '56px',
                  background: '#7F1D1D',
                  color: '#FEF2F2',
                  borderRadius: '8px',
                  padding: '4px 12px',
                  fontSize: '12px',
                  fontWeight: 700,
                }}
              >
                JOB ERROR #{index + 1}
              </Box>

              {/* Job Name */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '16px',
                }}
              >
                <WarningIcon sx={{ color: '#EF4444', fontSize: '24px' }} />
                <Box
                  sx={{
                    fontSize: '20px',
                    fontWeight: 700,
                    color: '#F1F5F9',
                  }}
                >
                  {error.jobName}
                </Box>
              </Box>

              {/* Error Message */}
              <Box
                sx={{
                  background: '#0F172A',
                  border: '1px solid #334155',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px',
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  color: '#FCA5A5',
                  lineHeight: 1.6,
                }}
              >
                {error.errorMessage || 'No error message available'}
              </Box>

              {/* Timestamp */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ fontSize: '13px', color: '#94A3B8' }}>
                  🕐 {new Date(error.timestamp).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })}
                </Box>
                <Chip
                  label={error.emailSent ? 'ADMINS NOTIFIED' : 'SCHEDULED JOB FAILED'}
                  size="small"
                  sx={{
                    background: error.emailSent ? '#F59E0B' : '#EF4444',
                    color: '#FFF',
                    fontWeight: 700,
                    fontSize: '11px',
                  }}
                />
              </Box>
            </Box>
          ))}

          {/* Manager Intel Sync Errors */}
          {syncErrors.map((error, index) => (
            <Box
              key={error.id}
              sx={{
                background: '#1E293B',
                border: '2px solid #7F1D1D',
                borderRadius: '12px',
                padding: '24px',
                position: 'relative',
                '&:hover': {
                  borderColor: '#EF4444',
                  boxShadow: '0 8px 24px #EF444444',
                },
              }}
            >
              {/* Dismiss Button */}
              <IconButton
                onClick={() => handleClearError(error.sleeperUsername)}
                disabled={loading}
                sx={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  color: '#94A3B8',
                  '&:hover': {
                    color: '#EF4444',
                    background: '#7F1D1D',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>

              {/* Error Number Badge */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '16px',
                  right: '56px',
                  background: '#7F1D1D',
                  color: '#FEF2F2',
                  borderRadius: '8px',
                  padding: '4px 12px',
                  fontSize: '12px',
                  fontWeight: 700,
                }}
              >
                SYNC ERROR #{index + 1}
              </Box>

              {/* Username */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '16px',
                }}
              >
                <WarningIcon sx={{ color: '#EF4444', fontSize: '24px' }} />
                <Box
                  component="a"
                  href={`/manager-intel/${error.sleeperUsername}`}
                  target="_blank"
                  sx={{
                    fontSize: '20px',
                    fontWeight: 700,
                    color: '#F1F5F9',
                    textDecoration: 'none',
                    '&:hover': {
                      color: '#3B82F6',
                    },
                  }}
                >
                  @{error.sleeperUsername}
                </Box>
              </Box>

              {/* Error Message */}
              <Box
                sx={{
                  background: '#0F172A',
                  border: '1px solid #334155',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px',
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  color: '#FCA5A5',
                  lineHeight: 1.6,
                }}
              >
                {error.errorMessage || 'No error message available'}
              </Box>

              {/* Timestamp */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ fontSize: '13px', color: '#94A3B8' }}>
                  🕐 {new Date(error.timestamp).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })}
                </Box>
                <Chip
                  label="SYNC FAILED"
                  size="small"
                  sx={{
                    background: '#EF4444',
                    color: '#FFF',
                    fontWeight: 700,
                    fontSize: '11px',
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            background: '#1E293B',
            border: '1px solid #334155',
            borderRadius: '12px',
            padding: '48px',
            textAlign: 'center',
          }}
        >
          <Box sx={{ fontSize: '64px', marginBottom: '16px' }}>🎉</Box>
          <Box sx={{ fontSize: '20px', fontWeight: 700, color: '#10B981', marginBottom: '8px' }}>
            All Systems Operational
          </Box>
          <Box sx={{ fontSize: '14px', color: '#94A3B8' }}>
            No errors detected in recent sync operations
          </Box>
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        PaperProps={{
          sx: {
            background: '#1E293B',
            border: '2px solid #334155',
            color: '#F1F5F9',
            minWidth: '400px',
          },
        }}
      >
        <DialogTitle sx={{ color: '#F1F5F9', fontWeight: 700, fontSize: '20px' }}>
          {dialogContent.title}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ color: '#94A3B8', fontSize: '16px', paddingTop: '8px' }}>
            {dialogContent.message}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{
              background: '#3B82F6',
              color: '#FFF',
              fontWeight: 700,
              padding: '8px 24px',
              '&:hover': {
                background: '#2563EB',
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ErrorTrackingTab;
